/* eslint-disable @typescript-eslint/no-explicit-any */
import NextImage from 'next/legacy/image'
import { useCallback, useMemo, useState, type ReactNode } from 'react'
import { useFilter } from 'react-aria'
import { Item } from 'react-stately'
import { useTranslate } from '../../contexts/TranslationContext'
import { ComboBox } from './ComboBox'
import style from './ComboBox.module.sass'
type UseState<Value> = readonly [Value, (value: Value) => void]

export function ControlledComboBox(props: {
	placeholder?: string | undefined
	items: { id: string; imageUrl?: string; label: string; alias?: string }[]
	windowed?: boolean
	name?: string
	height?: number
	state: UseState<string | null>
	isClearable?: boolean
	noOptionsMessage?: ReactNode
	hasEmptyOption?: boolean
	isNotSearchable?: boolean
	hasPreview?: boolean
	hasMinLength?: boolean
	label?: JSX.Element
	isArrowHidden?: boolean
	isResetedState?: UseState<boolean>
}) {
	const currentValue = props.state[0]
	const translate = useTranslate()

	const options = useMemo(
		() =>
			props.items.map((item) => ({
				value: item.id,
				key: item.id,
				label: item.label,
				imageUrl: item.imageUrl ?? null,
				alias: item.alias,
			})),
		[props.items],
	)

	const emptyOptionIncludedList = useMemo(
		() => [
			{
				key: 'undisclosed',
				value: 'undisclosed',
				label: translate('select.dontKnow'),
				alias: translate('select.dontKnow.aliases'),
				imageUrl: null,
			},
			...options,
		],
		[options, translate],
	)

	const optionList = props.hasEmptyOption ? emptyOptionIncludedList : options

	const value = useMemo(() => optionList.find((item) => item.value === currentValue), [currentValue, optionList])

	const { contains } = useFilter({ sensitivity: 'base' })
	const [filterValue, setFilterValue] = useState(value?.label ?? undefined)

	const conditionedFilterValue = props.hasMinLength ? filterValue && filterValue.length > 2 : filterValue
	const filteredItems = useMemo(
		() =>
			optionList.filter(
				(item) =>
					item.value === currentValue ||
					(filterValue && conditionedFilterValue && contains(item.label, filterValue.trim())) ||
					(item.alias && filterValue && conditionedFilterValue && contains(item.alias, filterValue.trim())),
			),
		[conditionedFilterValue, contains, currentValue, filterValue, optionList],
	)

	const [showAll, setShowAll] = useState(false)

	const handleChange = useCallback(
		(id: string | null) => {
			if (!id) {
				id = ''
			}
			props.state[1](id)
			setFilterValue(optionList.find((item) => item.value === id)?.label)
		},
		[optionList, props.state],
	)

	const id = props.state[0]

	return (
		<>
			<ComboBox
				onOpenChange={(isOpen: boolean, menuTrigger: string | undefined) => {
					if (menuTrigger === 'manual' && isOpen) {
						setShowAll(true)
					}
				}}
				name={props.name}
				isResetedState={props.isResetedState}
				isArrowHidden={props.isArrowHidden}
				placeholder={props.placeholder}
				hasMinLength={props.hasMinLength}
				hasPreview={props.hasPreview}
				height={props.height}
				label={props.label}
				clearInput={() => {
					setFilterValue(undefined)
					handleChange(null)
				}}
				noOptionsMessage={props.noOptionsMessage}
				items={showAll ? optionList : filteredItems}
				inputValue={filterValue}
				onSelectionChange={(id: any) => {
					handleChange(id as string | null)
				}}
				imagePreview={
					value?.imageUrl && (
						<div className={style.previewWrapper}>
							<NextImage
								src={value.imageUrl}
								alt={translate('alternativeText.productImage')}
								title={value.label}
								layout="fill"
								objectFit="contain"
							/>
						</div>
					)
				}
				selectedPreview={Boolean(value?.imageUrl)}
				onKeyDown={(e: KeyboardEvent) => {
					if (filterValue?.length === 1) {
						if (e.key === 'Backspace' || e.key === 'Delete') {
							handleChange(null)
						}
					}
				}}
				onInputChange={(value: string) => {
					setShowAll(false)
					if (value || !id) {
						setFilterValue(value)
					}
				}}
			>
				{(item: any) => <Item>{item.label}</Item>}
			</ComboBox>
		</>
	)
}
